import { useCallback, useEffect, useMemo, useState } from "react";
import { useGlobalContext } from "../data/GlobalContext";
import { encrypt } from "../data/CryptoUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faChevronRight,
	faChevronLeft,
	faGift,
} from "@fortawesome/free-solid-svg-icons";
import { apiJSONURL } from "../../App";

const VOUCHER_TYPE_CONFIG = {
	FREE_CREDITS: {
		cardTitle: "Credit Voucher",
		amountLabel: "Free Credits",
		amountFormat: "currency",
		mascotSrc: "/images/character/FGH-CHARACTER_VOUCHER-E_GIFT.webp",

		mascotAlt: "Free credits voucher mascot",
	},

	E_GIFT: {
		cardTitle: "E-Gift Voucher",
		amountLabel: "E-Gift",
		amountFormat: "currency",
		mascotSrc: "/images/character/FGH-CHARACTER_VOUCHER-FREE_CREDITS.webp",
		mascotAlt: "E-gift voucher mascot",
	},

	FREE_SPINS: {
		cardTitle: "Spin Voucher",
		amountLabel: "Free Spins",
		amountFormat: "number",
		mascotSrc: "/images/character/FGH-CHARACTER_VOUCHER-FREE_SPINS.webp",
		mascotAlt: "Free spins voucher mascot",
	},

	GCASH: {
		cardTitle: "E-Gift Voucher",
		amountLabel: "E-Gift",
		amountFormat: "number",
		mascotSrc: "/images/character/FGH_VOUCHER-E_GIFT-GCASH.webp",
		mascotAlt: "Free Voucher E-Gift",
	},
	MAYA: {
		cardTitle: "E-Gift Voucher",
		amountLabel: "E-Gift",
		amountFormat: "number",
		mascotSrc: "/images/character/FGH_VOUCHER-E_GIFT-MAYA.webp",
		mascotAlt: "Free Voucher E-Gift",
	},
};

const DEFAULT_VOUCHER_TYPE = "FREE_CREDITS";

const tabList = [
	{ id: 1, tab: "Vouchers", section: "vouchers-tab" },
	{ id: 2, tab: "History", section: "history-tab" },
];

const numberFormatter = new Intl.NumberFormat("en-PH", {
	maximumFractionDigits: 0,
});

const currencyFormatter = new Intl.NumberFormat("en-PH", {
	style: "currency",
	currency: "PHP",
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

const dateFormatter = new Intl.DateTimeFormat("en-PH", {
	year: "numeric",
	month: "long",
	day: "numeric",
});

const timeFormatter = new Intl.DateTimeFormat("en-PH", {
	hour: "numeric",
	minute: "2-digit",
	hour12: true,
});

const normalizeVoucherType = (value) => {
	const normalizedValue = String(value || "")
		.trim()
		.toUpperCase()
		.replace(/[\s-]+/g, "_");

	return VOUCHER_TYPE_CONFIG[normalizedValue]
		? normalizedValue
		: DEFAULT_VOUCHER_TYPE;
};

const formatVoucherHistory = (items = []) => {
	return items.map((item, index) => {
		const claimedAt = item.timeClaimed
			? new Date(item.timeClaimed.replace(" ", "T"))
			: null;

		const hasValidDate =
			claimedAt instanceof Date && !Number.isNaN(claimedAt.getTime());

		const voucherType = normalizeVoucherType(item.voucherType);
		const voucherTypeConfig = VOUCHER_TYPE_CONFIG[voucherType];

		const numericAmount = Number(item.amount);
		const validAmount = Number.isFinite(numericAmount) ? numericAmount : 0;

		const formattedAmount =
			voucherTypeConfig.amountFormat === "currency"
				? currencyFormatter.format(validAmount)
				: numberFormatter.format(validAmount);

		return {
			id: item.voucherId || `${item.voucherName}-${item.timeClaimed}-${index}`,

			voucherId: item.voucherId || "",
			title: item.voucherName || voucherTypeConfig.cardTitle,

			voucherType,
			cardTitle: voucherTypeConfig.cardTitle,
			amountLabel: voucherTypeConfig.amountLabel,
			amount: formattedAmount,

			mascotSrc: voucherTypeConfig.mascotSrc,
			mascotAlt: voucherTypeConfig.mascotAlt,

			date: hasValidDate ? dateFormatter.format(claimedAt) : "Unknown date",

			time: hasValidDate ? timeFormatter.format(claimedAt) : "Unknown time",

			status: "Claimed",
		};
	});
};

export default function ClaimVoucher({ onClose }) {
	const { username, refreshWalletBalances } = useGlobalContext();
	const [voucher, setVoucher] = useState("");
	const [message, setMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [activeSection, setActiveSection] = useState("vouchers-tab");
	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const [searchLoading, setSearchLoading] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);
	const [voucherHistory, setVoucherHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyError, setHistoryError] = useState("");
	const [windowWidth, setWindowWidth] = useState(() =>
		typeof window !== "undefined" ? window.innerWidth : 1024,
	);
	const [currentPage, setCurrentPage] = useState(1);
	const vouchersPerPage = 6;
	const isLoggedIn = Boolean(username);

	const loadVoucherHistory = useCallback(
		async ({ signal } = {}) => {
			if (!username) {
				setVoucherHistory([]);
				setHistoryError("");
				setHistoryLoading(false);
				return;
			}

			setHistoryLoading(true);
			setHistoryError("");

			try {
				const encryptedUsername = await encrypt(username);

				if (signal?.aborted) return;

				const response = await fetch(
					`${apiJSONURL}?cmd=voucherHistory&u=${encodeURIComponent(
						encryptedUsername,
					)}`,
					{
						method: "GET",
						signal,
					},
				);

				if (!response.ok) {
					throw new Error(`HTTP Error: ${response.status}`);
				}

				const result = await response.json();

				if (!Array.isArray(result?.data)) {
					throw new Error(
						"You don't have any vouchers at the moment. Participate in events, complete missions, and claim rewards to fill your collection.",
					);
				}

				setVoucherHistory(formatVoucherHistory(result.data));
			} catch (error) {
				if (error instanceof DOMException && error.name === "AbortError") {
					return;
				}

				console.error("Failed to fetch voucher history:", error);

				setVoucherHistory([]);
				setHistoryError(
					error instanceof Error
						? error.message
						: "Unable to load voucher history.",
				);
			} finally {
				if (!signal?.aborted) {
					setHistoryLoading(false);
				}
			}
		},
		[username],
	);

	const filteredVoucherHistory = useMemo(() => {
		const search = debouncedSearch.trim().toLowerCase();

		if (!search) {
			return voucherHistory;
		}

		return voucherHistory.filter((item) => {
			return [
				item.title,
				item.amount,
				item.amountLabel,
				item.cardTitle,
				item.voucherType,
				item.date,
				item.time,
				item.status,
				item.voucherId,
			].some((value) => String(value).toLowerCase().includes(search));
		});
	}, [debouncedSearch, voucherHistory]);

	const totalPages = Math.ceil(filteredVoucherHistory.length / vouchersPerPage);

	const startIndex = (currentPage - 1) * vouchersPerPage;

	const paginatedVoucherHistory = filteredVoucherHistory.slice(
		startIndex,
		startIndex + vouchersPerPage,
	);
	const handlePageChange = (page) => {
		if (page >= 1 && page <= totalPages) {
			setCurrentPage(page);
		}
	};

	const sanitizeVoucher = (value) => value.replace(/[^a-zA-Z0-9]/g, "");

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (loading) return;

		if (!isLoggedIn) {
			setMessage("Please log in to claim a voucher.");
			return;
		}

		const voucherCode = sanitizeVoucher(voucher);

		if (!voucherCode) {
			setMessage("Please fill in the voucher code.");
			return;
		}

		setMessage("");
		setLoading(true);
		setIsSuccess(false);

		try {
			const encryptedUsername = await encrypt(username);

			const formData = new URLSearchParams();
			formData.append("cmd", "redeemVoucher");
			formData.append("u", encryptedUsername);
			formData.append("voucherId", voucherCode);

			const response = await fetch(apiJSONURL, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				body: formData.toString(),
			});

			if (!response.ok) {
				throw new Error(`Server error (${response.status})`);
			}

			const data = await response.json();

			if (data?.isRedeemSuccess) {
				// Fetch the updated balance after successful redemption
				await refreshWalletBalances(username);

				// Optionally refresh voucher history too
				await loadVoucherHistory();

				setMessage("Voucher redeemed successfully.");
				setVoucher("");
				setIsSuccess(true);
			} else {
				setMessage(data?.message || "Invalid voucher. Please try again!");
				setVoucher("");
			}
		} catch (error) {
			console.error("Redeem Voucher Error:", error);

			setMessage(
				error instanceof Error
					? error.message
					: "Unable to redeem voucher. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const handleCopyVoucher = async (voucherId) => {
		if (!voucherId) return;

		try {
			await navigator.clipboard.writeText(String(voucherId));

			setMessage("Voucher ID copied to clipboard!");

			// Optional: remove the message after 2 seconds
			setTimeout(() => {
				setMessage("");
			}, 2000);
		} catch (error) {
			console.error("Failed to copy voucher ID:", error);
			setMessage("Unable to copy voucher ID.");
		}
	};

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		setSearchLoading(true);

		const timer = setTimeout(() => {
			setDebouncedSearch(searchTerm);
			setSearchLoading(false);
		}, 300); // adjust delay (200-500ms)

		return () => clearTimeout(timer);
	}, [searchTerm]);

	useEffect(() => {
		if (!message) return;

		const timer = setTimeout(() => {
			setMessage("");
		}, 6000);

		return () => clearTimeout(timer);
	}, [message]);

	useEffect(() => {
		if (activeSection !== "history-tab") return;

		const controller = new AbortController();

		loadVoucherHistory({
			signal: controller.signal,
		});

		return () => {
			controller.abort();
		};
	}, [activeSection, loadVoucherHistory]);

	useEffect(() => {
		setCurrentPage(1);
	}, [debouncedSearch, voucherHistory.length]);

	return (
		<div className="openProfileItems-container">
			{windowWidth <= 840 && (
				<div className="close-profile-module-container">
					<div className="profile-logo">
						<a href="/">
							<img src="/images/icon/fgh-logo.webp" alt="Fun Gaming Hub" />
						</a>
					</div>

					<button
						type="button"
						className="close-profile-module-btn"
						onClick={onClose}
					>
						<img src="/images/icon/ARROW.webp" alt="" aria-hidden="true" />
						Back
					</button>
				</div>
			)}

			<div className="change-password-content">
				<div className="openProfile-Label">Claim Voucher</div>

				<div className="wallet-section-3">
					<ul className="profile-tablist">
						{tabList.map((item) => (
							<li
								key={item.id}
								onClick={() => setActiveSection(item.section)}
								className={activeSection === item.section ? "active" : ""}
							>
								{item.tab}
							</li>
						))}
					</ul>

					{activeSection === "vouchers-tab" && (
						<div className="wallet-section-3">
							<div className="change-password-container">
								<div className="change-password-container--notice">
									Notice: Enter your E-Voucher code below to top up your
									account. Make sure the code is correct before submitting.
								</div>

								<form
									onSubmit={handleSubmit}
									className="transaction-form"
									autoComplete="off"
								>
									<div
										className={`password-field ${isSuccess ? "success" : ""}`}
									>
										<input
											type="text"
											placeholder="Enter your code"
											name="voucher"
											id="voucher"
											value={voucher}
											onChange={(e) => {
												setVoucher(sanitizeVoucher(e.target.value));
												setIsSuccess(false);
											}}
										/>

										{isSuccess && <span className="check-icon">✓</span>}
									</div>

									{message && <div className="voucher-message">{message}</div>}

									{/* <div className="transaction-field-message">
										Step 1: <b>Purchase a voucher</b> Step 2:{" "}
										<b>Receive your code</b> Step 3: <b>Top up your account.</b>
									</div> */}

									<div className="claim-voucher">
										<button
											type="submit"
											className={`change-password-btn ${
												sanitizeVoucher(voucher) === "" ? "disabled" : ""
											}`}
											disabled={sanitizeVoucher(voucher) === "" || loading}
										>
											{loading ? "Processing..." : "Submit"}
										</button>
									</div>
								</form>
							</div>
						</div>
					)}

					{activeSection === "history-tab" && (
						<section className="voucher-history-section">
							<div className="voucher-history-header">
								{filteredVoucherHistory.length === 0 ? null : (
									<span className="voucher-history-count">
										{filteredVoucherHistory.length}{" "}
										{filteredVoucherHistory.length === 1
											? "voucher"
											: "vouchers"}
									</span>
								)}

								<div className="voucher-history-search">
									<label htmlFor="voucher-history-search" className="sr-only">
										Search voucher history
									</label>

									<svg
										className="voucher-search-icon"
										viewBox="0 0 24 24"
										aria-hidden="true"
									>
										<path
											d="m21 21-4.35-4.35m2.35-5.15a7.5 7.5 0 1 1-15 0 7.5 7.5 0 0 1 15 0Z"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
										/>
									</svg>

									<input
										type="search"
										id="voucher-history-search"
										placeholder="Search vouchers..."
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										autoComplete="off"
									/>

									{searchTerm && (
										<button
											className="voucher-search-clear"
											type="button"
											onClick={() => setSearchTerm("")}
											aria-label="Clear voucher search"
										>
											×
										</button>
									)}
								</div>
							</div>

							{historyLoading ? (
								<div className="voucher-history-loading">
									<div className="spinner" />
									<p>Loading voucher history...</p>
								</div>
							) : historyError ? (
								<div className="voucher-history-empty">
									<img src="/images/NO_VOUCHER.webp" alt="" />

									<h2>
										No <span>Vouchers</span> Found
									</h2>

									<p className="no-voucher">{historyError}</p>

									<a href="/promotion">
										<FontAwesomeIcon icon={faGift} />
										Explore Promotions
										<FontAwesomeIcon icon={faChevronRight} />
									</a>
								</div>
							) : searchLoading ? (
								<div className="voucher-history-loading">
									<div className="spinner" />
									<p>Searching vouchers...</p>
								</div>
							) : filteredVoucherHistory.length === 0 ? (
								<div className="voucher-history-empty">
									<img src="/images/NO_VOUCHER.webp" alt="" />

									<h2>
										No <span>Vouchers</span> Found
									</h2>

									{debouncedSearch ? (
										<p className="no-history">
											No voucher history matched "{debouncedSearch}".
										</p>
									) : (
										<p className="no-voucher">
											You don't have any vouchers at the moment. Participate in
											events, complete missions, and claim rewards to fill your
											collection.
										</p>
									)}

									{debouncedSearch ? (
										<button
											type="button"
											className="voucher-history-retry-btn"
											onClick={() => setSearchTerm("")}
										>
											Clear Search
										</button>
									) : (
										<a href="/promotion">
											<FontAwesomeIcon icon={faGift} />
											Explore Promotions
											<FontAwesomeIcon icon={faChevronRight} />
										</a>
									)}
								</div>
							) : (
								<>
									<div className="voucher-history-grid">
										{paginatedVoucherHistory.map((item) => (
											<article
												className={`voucher-card voucher-card--${item.voucherType
													.toLowerCase()
													.replaceAll("_", "-")}`}
												key={item.id}
											>
												<div className="voucher-brand">
													<div
														className={`voucher-glow voucher-glow--${item.voucherType
															.toLowerCase()
															.replaceAll("_", "-")}`}
													/>
													<div
														className={`voucher-stat voucher-stat--${item.voucherType
															.toLowerCase()
															.replaceAll("_", "-")}`}
													>
														<span>{item.cardTitle}</span>
													</div>
													<div className="voucher-amount">
														<div
															className={`voucher-amount--amount voucher-amount--amount--${item.voucherType
																.toLowerCase()
																.replaceAll("_", "-")}`}
														>
															{item.amount}
														</div>
														<div className="voucher-amount--label">
															{item.amountLabel}
														</div>
													</div>
												</div>

												<div
													className={`voucher-separator voucher-separator--${item.voucherType
														.toLowerCase()
														.replaceAll("_", "-")}`}
													aria-hidden="true"
												>
													<span />
													<span />
													<span />
													<span />
													<span />
												</div>

												<div className="voucher-content">
													<div className="voucher-mascot">
														<img
															src={item.mascotSrc}
															alt={item.mascotAlt}
															loading="lazy"
														/>
													</div>
													<div className="voucher-heading">
														<div className="voucher-heading-text">
															<p className="voucher-label">Voucher Code</p>
														</div>

														<span className="voucher-status">
															{item.status}
														</span>
													</div>
													<p
														className={`voucher-value voucher-value--${item.voucherType
															.toLowerCase()
															.replaceAll("_", "-")}`}
													>
														<div
															className="voucher-value--clipboard"
															onClick={() =>
																handleCopyVoucher(
																	String(item.voucherId).slice(0, 7),
																)
															}
															role="button"
															tabIndex={0}
															aria-label="Copy voucher ID"
														>
															<img
																src="/images/icon/copy-regular-full.svg"
																alt=""
															/>
														</div>

														{String(item.voucherId).slice(0, 7)}
													</p>
													<div className="voucher-footer--container">
														<div
															className={`voucher-footer voucher-footer--${item.voucherType
																.toLowerCase()
																.replaceAll("_", "-")}`}
														>
															<span
																className={`voucher-footer--tag voucher-footer--tag--${item.voucherType
																	.toLowerCase()
																	.replaceAll("_", "-")}`}
															>
																Claimed On
															</span>
															<div
																className={`voucher-footer--content voucher-footer--content--${item.voucherType
																	.toLowerCase()
																	.replaceAll("_", "-")}`}
															>
																<span
																	className={`voucher-date voucher-date--${item.voucherType
																		.toLowerCase()
																		.replaceAll("_", "-")}`}
																>
																	<svg viewBox="0 0 24 24" aria-hidden="true">
																		<path d="M7 2v3M17 2v3M3.5 9h17M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
																	</svg>

																	{item.date}
																</span>

																<span className="voucher-time">
																	<svg viewBox="0 0 24 24" aria-hidden="true">
																		<circle cx="12" cy="12" r="9" />
																		<path d="M12 7v5l3 2" />
																	</svg>

																	{item.time}
																</span>
															</div>
														</div>
														{/* <div className="voucher-footer">
                              <span className="voucher-footer--tag">
                                Valid Until
                              </span>
                              <div className="voucher-footer--content">
                                <span className="voucher-date">
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path d="M7 2v3M17 2v3M3.5 9h17M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
                                  </svg>
                                  September 3, 2026
                                </span>

                                <span className="voucher-time">
                                  <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M12 7v5l3 2" />
                                  </svg>
                                  12:00 PM
                                </span>
                              </div>
                            </div> */}
													</div>
												</div>

												{/* <div
                          className={`voucher-decoration voucher-decoration--${item.voucherType
                            .toLowerCase()
                            .replaceAll("_", "-")}`}
                          aria-hidden="true"
                        >
                          <span>FGH</span>
                        </div> */}
											</article>
										))}
									</div>

									{totalPages > 1 && (
										<nav
											className="voucher-pagination"
											aria-label="Voucher history pagination"
										>
											<button
												type="button"
												onClick={() => handlePageChange(currentPage - 1)}
												disabled={currentPage === 1}
												className="fgh-main-pagination-btn"
												aria-label="Previous page"
											>
												<FontAwesomeIcon
													icon={faChevronLeft}
													className="custom-next-btn"
												/>
											</button>

											{Array.from({ length: totalPages }, (_, index) => {
												const page = index + 1;

												return (
													<button
														type="button"
														key={page}
														onClick={() => handlePageChange(page)}
														className={currentPage === page ? "active" : ""}
														aria-current={
															currentPage === page ? "page" : undefined
														}
														aria-label={`Go to page ${page}`}
													>
														{page}
													</button>
												);
											})}

											<button
												type="button"
												onClick={() => handlePageChange(currentPage + 1)}
												disabled={currentPage === totalPages}
												className="fgh-main-pagination-btn"
												aria-label="Next page"
											>
												<FontAwesomeIcon
													icon={faChevronRight}
													className="custom-next-btn"
												/>
											</button>
										</nav>
									)}
								</>
							)}
						</section>
					)}
				</div>
			</div>
		</div>
	);
}
